import React, { useState, useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';

const STATUS_STEPS = [
  { key: 'open', label: 'Open' },
  { key: 'in_review', label: 'In Review' },
  { key: 'awaiting_response', label: 'Awaiting Response' },
  { key: 'resolved', label: 'Resolved' },
  { key: 'closed', label: 'Closed' },
];

const STATUS_CONFIG = {
  open: { label: 'Open', bg: '#fef3c7', color: '#d97706' },
  in_review: { label: 'In Review', bg: '#dbeafe', color: '#1d4ed8' },
  awaiting_response: { label: 'Awaiting Response', bg: '#ede9fe', color: '#6d28d9' },
  resolved: { label: 'Resolved', bg: '#d1fae5', color: '#065f46' },
  closed: { label: 'Closed', bg: '#f3f4f6', color: '#374151' },
};

const CATEGORY_COLORS = {
  rides: '#3b82f6',
  deliveries: '#8b5cf6',
  payments: '#10b981',
  driver_issues: '#f59e0b',
  account: '#ec4899',
  general: '#6b7280',
};

const ChatInterface = ({ ticket, messages, onMessageSent, onStatusChange }) => {
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d?.toLocaleString('en-KE', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  const handleSend = async () => {
    if (!replyText?.trim() || !ticket?.id) return;
    setSending(true);
    try {
      const { error } = await supabase?.from('ticket_messages')?.insert({
          ticket_id: ticket?.id,
          sender_name: 'Support Agent',
          sender_role: 'agent',
          message: replyText?.trim(),
        });
      if (!error) {
        setReplyText('');
        onMessageSent?.();
      }
    } catch (err) {
      console.error('Send message error:', err);
    } finally {
      setSending(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    if (!ticket?.id || updatingStatus) return;
    setUpdatingStatus(true);
    try {
      const { error } = await supabase?.from('support_tickets')?.update({ ticket_status: newStatus, updated_at: new Date()?.toISOString() })?.eq('id', ticket?.id);
      if (!error) {
        onStatusChange?.(newStatus);
      }
    } catch (err) {
      console.error('Status update error:', err);
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e?.key === 'Enter' && !e?.shiftKey) {
      e?.preventDefault();
      handleSend();
    }
  };

  if (!ticket) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-gray-400">
        <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4" style={{ background: '#f0f4ff' }}>
          <Icon name="MessageSquare" size={28} color="#0f2044" />
        </div>
        <p className="text-base font-medium text-gray-600">Select a ticket to view conversation</p>
        <p className="text-sm text-gray-400 mt-1">Click any ticket from the list to open the chat</p>
      </div>
    );
  }

  const currentStatusIdx = STATUS_STEPS?.findIndex(s => s?.key === ticket?.ticket_status);
  const catColor = CATEGORY_COLORS?.[ticket?.category] || '#6b7280';
  const statusCfg = STATUS_CONFIG?.[ticket?.ticket_status] || STATUS_CONFIG?.open;

  return (
    <div className="flex flex-col h-full">
      {/* Ticket Header */}
      <div className="p-4 border-b border-gray-100" style={{ background: 'linear-gradient(135deg, #0f2044 0%, #1a3a6e 100%)' }}>
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs font-mono text-blue-200">{ticket?.ticket_number}</span>
              <span
                className="px-2 py-0.5 rounded text-xs font-medium"
                style={{ background: catColor + '30', color: '#fff' }}
              >
                {ticket?.category?.replace('_', ' ')}
              </span>
              <span
                className="px-2 py-0.5 rounded-full text-xs font-medium"
                style={{ background: statusCfg?.bg, color: statusCfg?.color }}
              >
                {statusCfg?.label}
              </span>
            </div>
            <h3 className="text-sm font-semibold text-white truncate">{ticket?.subject}</h3>
            <p className="text-xs text-blue-200 mt-0.5">
              {ticket?.user_name} · {ticket?.user_email}
              {ticket?.assigned_agent_name && ` · Agent: ${ticket?.assigned_agent_name}`}
            </p>
          </div>
          {/* Status Dropdown */}
          <div className="flex-shrink-0">
            <select
              value={ticket?.ticket_status || 'open'}
              onChange={e => handleStatusChange(e?.target?.value)}
              disabled={updatingStatus}
              className="text-xs rounded-lg px-2 py-1.5 border-0 focus:outline-none focus:ring-2 cursor-pointer"
              style={{ background: 'rgba(255,255,255,0.15)', color: '#fff' }}
            >
              {STATUS_STEPS?.map(s => (
                <option key={s?.key} value={s?.key} style={{ background: '#0f2044', color: '#fff' }}>
                  {s?.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Resolution Status Tracker */}
        <div className="mt-3">
          <div className="flex items-center gap-0">
            {STATUS_STEPS?.map((step, idx) => {
              const isCompleted = idx < currentStatusIdx;
              const isCurrent = idx === currentStatusIdx;
              return (
                <React.Fragment key={step?.key}>
                  <div className="flex flex-col items-center">
                    <div
                      className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-300"
                      style={{
                        background: isCompleted ? '#D4AF37' : isCurrent ? '#fff' : 'rgba(255,255,255,0.2)',
                        color: isCompleted ? '#0f2044' : isCurrent ? '#0f2044' : 'rgba(255,255,255,0.5)',
                        boxShadow: isCurrent ? '0 0 0 3px rgba(212,175,55,0.4)' : 'none',
                      }}
                    >
                      {isCompleted ? <Icon name="Check" size={12} /> : idx + 1}
                    </div>
                    <span className="text-xs mt-1 whitespace-nowrap" style={{ color: isCurrent ? '#D4AF37' : isCompleted ? '#D4AF37' : 'rgba(255,255,255,0.4)', fontSize: '9px' }}>
                      {step?.label}
                    </span>
                  </div>
                  {idx < STATUS_STEPS?.length - 1 && (
                    <div
                      className="flex-1 h-0.5 mx-1 mb-4 transition-all duration-300"
                      style={{ background: idx < currentStatusIdx ? '#D4AF37' : 'rgba(255,255,255,0.2)' }}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>
      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3" style={{ background: '#f8f9fc' }}>
        {messages?.length === 0 ? (
          <div className="flex items-center justify-center h-20 text-gray-400 text-sm">No messages yet</div>
        ) : (
          messages?.map(msg => {
            const isAgent = msg?.sender_role === 'agent';
            return (
              <div key={msg?.id} className={`flex ${isAgent ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-xs lg:max-w-sm xl:max-w-md`}>
                  <div className="flex items-center gap-1.5 mb-1" style={{ justifyContent: isAgent ? 'flex-end' : 'flex-start' }}>
                    <span className="text-xs font-medium text-gray-600">{msg?.sender_name}</span>
                    <span className="text-xs text-gray-400">{formatTime(msg?.created_at)}</span>
                  </div>
                  <div
                    className="px-3 py-2 rounded-2xl text-sm leading-relaxed"
                    style={{
                      background: isAgent ? 'linear-gradient(135deg, #0f2044, #1a3a6e)' : '#fff',
                      color: isAgent ? '#fff' : '#1f2937',
                      borderRadius: isAgent ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                    }}
                  >
                    {msg?.message}
                  </div>
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>
      {/* Reply Input */}
      <div className="p-3 border-t border-gray-100 bg-white">
        <div className="flex items-end gap-2">
          <div className="flex-1 border border-gray-200 rounded-xl overflow-hidden focus-within:ring-2" style={{ '--tw-ring-color': '#D4AF37' }}>
            <textarea
              value={replyText}
              onChange={e => setReplyText(e?.target?.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type your reply... (Enter to send, Shift+Enter for new line)"
              rows={2}
              className="w-full px-3 py-2 text-sm resize-none focus:outline-none"
            />
            <div className="flex items-center justify-between px-3 py-1.5 bg-gray-50 border-t border-gray-100">
              <div className="flex items-center gap-2">
                <button className="p-1 rounded hover:bg-gray-200 transition-colors" title="Attach file">
                  <Icon name="Paperclip" size={14} className="text-gray-500" />
                </button>
                <button className="p-1 rounded hover:bg-gray-200 transition-colors" title="Add emoji">
                  <Icon name="Smile" size={14} className="text-gray-500" />
                </button>
              </div>
              <span className="text-xs text-gray-400">{replyText?.length}/1000</span>
            </div>
          </div>
          <button
            onClick={handleSend}
            disabled={!replyText?.trim() || sending}
            className="flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ background: replyText?.trim() ? 'linear-gradient(135deg, #0f2044, #1a3a6e)' : '#e5e7eb' }}
          >
            {sending ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <Icon name="Send" size={16} color={replyText?.trim() ? '#D4AF37' : '#9ca3af'} />
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatInterface;
